import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { partnerApiService } from '@/services/partnerApiService'
import { 
  Filter, 
  ArrowUpRight, 
  DollarSign,
  Calendar,
  Wallet
} from 'lucide-react'
import { fmtKrw, fmtUsdt } from '@/utils/currency'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

interface Partner {
  id: number
  name: string
  company?: string
  can_view_users: boolean
  can_view_trading: boolean
  can_view_deposits: boolean
  can_view_withdrawals: boolean
  can_view_fees: boolean
  can_view_statistics: boolean
}

interface Withdrawal {
  id: number
  user_email: string
  user_nickname: string
  user_name: string
  amount_usdt: number
  amount_krw: number
  wallet_address: string
  network: string
  status: string
  created_at: string
  approved_at?: string
  rejected_at?: string
  reject_reason?: string
  tx_hash?: string
}

interface WithdrawalsResponse {
  withdrawals: Withdrawal[]
  total: number
  page: number
  pages: number
}

const PartnerWithdrawals = () => {
  const { partner } = useOutletContext<{ partner: Partner }>()
  const { t, i18n } = useTranslation('withdrawals')
  const locale = i18n.language === 'ko' ? 'ko-KR' : i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'zh' ? 'zh-CN' : 'en-US'
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalWithdrawals, setTotalWithdrawals] = useState(0)

  // 권한 확인
  if (!partner.can_view_withdrawals) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">{t('common:noPermission')}</h2>
          <p className="text-dark-400">{t('userWithdrawals.noPermission')}</p>
        </div>
      </div>
    )
  }

  useEffect(() => {
    loadWithdrawals()
  }, [currentPage, statusFilter])

  const loadWithdrawals = async () => {
    try {
      setLoading(true)

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10'
      })

      if (statusFilter) {
        params.append('status', statusFilter)
      }

      console.log('🔍 PartnerWithdrawals: 출금 내역 요청 시작', { 
        currentPage, 
        statusFilter 
      })
      
      const data = await partnerApiService.getWithdrawals(
        currentPage, 
        10, 
        statusFilter || undefined
      )
      
      console.log('✅ PartnerWithdrawals: 출금 내역 가져오기 성공', data)
      
      setWithdrawals(data.withdrawals)
      setTotalPages(data.pages)
      setTotalWithdrawals(data.total)
    } catch (error) {
      console.error('Error loading withdrawals:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => fmtKrw(amount)

  const formatUSDT = (amount: number) => fmtUsdt(amount)

  // 순수 원화지갑 출금(amount_usdt=0)이면 원화를 주 금액으로 표시, 그 외는 USDT
  const isPureKrw = (w: Withdrawal) => Number(w.amount_krw) > 0 && Number(w.amount_usdt) === 0
  const formatPrimaryAmount = (w: Withdrawal) =>
    isPureKrw(w) ? fmtKrw(w.amount_krw) : fmtUsdt(w.amount_usdt)

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(locale)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-500/20 text-green-400 border-green-500/30'
      case 'PENDING':
      case 'USER_APPROVED':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      case 'REQUESTED':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      case 'REJECTED':
        return 'bg-red-500/20 text-red-400 border-red-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }
  }

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'REQUESTED': t('common:status.requested'),
      'PENDING': t('common:status.pending'),
      'USER_APPROVED': t('common:status.userApproved'),
      'APPROVED': t('common:status.approved'),
      'REJECTED': t('common:status.rejected')
    }
    return statusMap[status] || status
  }

  const truncateAddress = (address: string) => {
    if (!address) return '-'
    if (address.length <= 10) return address
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* 페이지 헤더 */}
      <div className="border-b border-white/10 pb-4 sm:pb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('userWithdrawals.title')}</h1>
        <p className="text-dark-400">{t('userWithdrawals.subtitle')}</p>
      </div>

      {/* 필터 */}
      <div className="glass-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-dark-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="pl-10 pr-8 py-3 bg-dark-800 border border-white/10 rounded-lg text-white focus:border-primary-500 focus:outline-none min-w-[150px]"
            >
              <option value="">{t('common:allStatus')}</option>
              <option value="REQUESTED">{t('userWithdrawals.pendingFilter')}</option>
              <option value="APPROVED">{t('userWithdrawals.approvedFilter')}</option>
              <option value="REJECTED">{t('userWithdrawals.rejectedFilter')}</option>
            </select>
          </div>
        </div>

        <div className="mt-4 text-sm text-dark-400">
          {t('userWithdrawals.totalWithdrawals', { count: totalWithdrawals })}
          {statusFilter && ` · ${t('userWithdrawals.statusOnly', { status: getStatusText(statusFilter) })}`}
        </div>
      </div>

      {/* 출금 목록 */}
      <div className="glass-card">
        <div className="p-4 sm:p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <ArrowUpRight className="w-5 h-5 mr-2" />
              {t('userWithdrawals.withdrawalHistory')}
            </h3>
            <div className="text-sm text-dark-400">
              {t('common:pageInfo', { current: currentPage, total: totalPages })}
            </div>
          </div>
        </div>

        {/* 데스크톱 테이블 */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('userWithdrawals.tableUserInfo')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('userWithdrawals.tableAmount')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('userWithdrawals.tableWallet')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('userWithdrawals.tableNetwork')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider w-24">
                  {t('userWithdrawals.tableStatus')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('userWithdrawals.tableRequestDate')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('userWithdrawals.tableProcessDate')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {withdrawals.map((withdrawal) => (
                <tr key={withdrawal.id} className="hover:bg-white/5 transition-colors duration-200">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-white">{withdrawal.user_nickname}</div>
                      <div className="text-sm text-dark-400">{withdrawal.user_email}</div>
                      <div className="text-xs text-dark-500">{withdrawal.user_name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center text-sm font-medium text-white">
                        <DollarSign className="w-4 h-4 mr-1 text-yellow-400" />
                        {formatPrimaryAmount(withdrawal)}
                      </div>
                      {!isPureKrw(withdrawal) && (
                        <div className="text-xs text-dark-400">
                          ≈ {formatCurrency(withdrawal.amount_krw)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm text-white">
                      <Wallet className="w-4 h-4 mr-1 text-dark-400" />
                      <span className="font-mono">{truncateAddress(withdrawal.wallet_address)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-white font-medium">
                      {withdrawal.network}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap w-24">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${getStatusColor(withdrawal.status)}`}>
                      {getStatusText(withdrawal.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm text-dark-300">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(withdrawal.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-dark-300">
                      {withdrawal.approved_at ? formatDate(withdrawal.approved_at) : 
                       withdrawal.rejected_at ? formatDate(withdrawal.rejected_at) : '-'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 모바일 카드 목록 */}
        <div className="lg:hidden divide-y divide-white/10">
          {withdrawals.map((withdrawal) => (
            <div key={withdrawal.id} className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="text-lg font-medium text-white">{withdrawal.user_nickname}</div>
                  <div className="text-sm text-dark-400">{withdrawal.user_email}</div>
                  <div className="text-xs text-dark-500 mt-1">{withdrawal.user_name}</div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(withdrawal.status)}`}>
                  {getStatusText(withdrawal.status)}
                </span>
              </div>
              
              <div className="grid grid-cols-1 gap-4 text-sm mb-4">
                <div>
                  <div className="text-dark-400 mb-1">{t('userWithdrawals.tableAmount')}</div>
                  <div className="flex items-center text-white font-medium">
                    <DollarSign className="w-4 h-4 mr-1 text-yellow-400" />
                    {formatPrimaryAmount(withdrawal)}
                  </div>
                  {!isPureKrw(withdrawal) && (
                    <div className="text-xs text-dark-400 mt-1">
                      ≈ {formatCurrency(withdrawal.amount_krw)}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-dark-400 mb-1">{t('userWithdrawals.tableWallet')}</div>
                  <div className="flex items-center text-white font-mono text-xs">
                    <Wallet className="w-4 h-4 mr-1 text-dark-400" />
                    {truncateAddress(withdrawal.wallet_address)}
                  </div>
                </div>
                <div>
                  <div className="text-dark-400 mb-1">{t('userWithdrawals.tableNetwork')}</div>
                  <div className="text-white">{withdrawal.network}</div>
                </div>
                <div>
                  <div className="text-dark-400 mb-1">{t('userWithdrawals.tableRequestDate')}</div>
                  <div className="text-white">{formatDate(withdrawal.created_at)}</div>
                </div>
              </div>
              
              {(withdrawal.approved_at || withdrawal.rejected_at) && (
                <div className="text-sm">
                  <div className="text-dark-400 mb-1">{t('userWithdrawals.tableProcessDate')}</div>
                  <div className="text-white">
                    {withdrawal.approved_at ? formatDate(withdrawal.approved_at) : 
                     withdrawal.rejected_at ? formatDate(withdrawal.rejected_at) : '-'}
                  </div>
                </div>
              )}
              
              {withdrawal.reject_reason && (
                <div className="text-sm mt-3">
                  <div className="text-dark-400 mb-1">{t('userWithdrawals.rejectReason')}</div>
                  <div className="text-red-400">{withdrawal.reject_reason}</div>
                </div>
              )}

              {withdrawal.tx_hash && (
                <div className="text-sm mt-3">
                  <div className="text-dark-400 mb-1">{t('userWithdrawals.txHash')}</div>
                  <div className="text-white font-mono text-xs">{truncateAddress(withdrawal.tx_hash)}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 빈 상태 */}
        {withdrawals.length === 0 && (
          <div className="p-8 sm:p-12 text-center">
            <ArrowUpRight className="w-12 h-12 text-dark-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">{t('userWithdrawals.noWithdrawals')}</h3>
            <p className="text-dark-400">
              {statusFilter ? t('userWithdrawals.noStatusWithdrawals') : t('userWithdrawals.noWithdrawalsYet')}
            </p>
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-dark-800 border border-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-dark-700 transition-colors duration-200"
          >
            {t('common:previous')}
          </button>
          
          <div className="flex space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const maxVisible = 5
              const page = Math.max(1, Math.min(totalPages - (maxVisible - 1), currentPage - Math.floor(maxVisible / 2))) + i
              if (page > totalPages) return null
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-4 py-2 border border-white/10 rounded-lg transition-colors duration-200 ${
                    currentPage === page
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-dark-800 text-white hover:bg-dark-700'
                  }`}
                >
                  {page}
                </button>
              )
            })}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-dark-800 border border-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-dark-700 transition-colors duration-200"
          >
            {t('common:next')}
          </button>
        </div>
      )}
    </div>
  )
}

export default PartnerWithdrawals